"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_REPLACEMENT_LENGTH = exports.MAX_PATTERN_LENGTH = void 0;
exports.transform = transform;
exports.transformBatch = transformBatch;
const get_1 = require("../get");
const is_directive_1 = require("./is-directive");
const placeholders_1 = require("./placeholders");
const real_type_of_1 = require("../real-type-of");
const remove_undefined_1 = require("../remove-undefined");
const validate_1 = __importDefault(require("./validate"));
const arrify_1 = require("../arrify");
const flatten_1 = require("./flatten");
const liquid_directive_1 = require("./liquid-directive");
const value_keys_1 = require("./value-keys");
const ROOT_MAPPING_FIELD_KEY = '__segment_internal_directive';
const directives = {};
const directiveRegExp = /^@[a-z][a-zA-Z0-9]+$/;
function registerDirective(name, fn) {
    if (!directiveRegExp.exec(name)) {
        throw new Error(`"${name}" is an invalid directive name`);
    }
    directives[name] = fn;
}
function registerStringDirective(name, fn) {
    registerDirective(name, (value, payload) => {
        const str = resolve(value, payload);
        if (typeof str !== 'string') {
            throw new Error(`${name}: expected string, got ${(0, real_type_of_1.realTypeOf)(str)}`);
        }
        return fn(str, payload);
    });
}
function runDirective(obj, payload, statsContext) {
    const name = Object.keys(obj).find((key) => key.startsWith('@'));
    const directiveFn = directives[name];
    const value = obj[name];
    if (typeof directiveFn !== 'function') {
        throw new Error(`${name} is not a valid directive, got ${(0, real_type_of_1.realTypeOf)(directiveFn)}`);
    }
    if (name === '@liquid') {
        return directiveFn(value, payload, statsContext);
    }
    return directiveFn(value, payload);
}
registerDirective('@if', (opts, payload) => {
    let condition = false;
    if (!(0, real_type_of_1.isObject)(opts)) {
        throw new Error('@if requires an object with an "exists" key');
    }
    if (!opts.exists && !opts.blank) {
        throw new Error('@if requires an "exists" key or a "blank" key');
    }
    else if (opts.exists !== undefined) {
        const value = resolve(opts.exists, payload);
        condition = value !== undefined && value !== null;
    }
    else if (opts.blank !== undefined) {
        const value = resolve(opts.blank, payload);
        condition = value !== undefined && value !== null && value != '';
    }
    if (condition && opts.then !== undefined) {
        return resolve(opts.then, payload);
    }
    else if (!condition && opts.else) {
        return resolve(opts.else, payload);
    }
});
registerDirective('@case', (opts, payload) => {
    if (!(0, real_type_of_1.isObject)(opts)) {
        throw new Error('@case requires an object with a "operator" key');
    }
    if (!opts.operator) {
        throw new Error('@case requires a "operator" key');
    }
    const operator = opts.operator;
    if (opts.value) {
        const value = resolve(opts.value, payload);
        if (typeof value === 'string') {
            switch (operator) {
                case 'lower':
                    return value.toLowerCase();
                case 'upper':
                    return value.toUpperCase();
                default:
                    throw new Error('operator key should have a value of "lower" or "upper"');
            }
        }
        return value;
    }
});
exports.MAX_PATTERN_LENGTH = 10;
exports.MAX_REPLACEMENT_LENGTH = 10;
function performReplace(value, pattern, replacement, flags) {
    if (pattern.length > exports.MAX_PATTERN_LENGTH) {
        throw new Error(`@replace requires a "pattern" less than ${exports.MAX_PATTERN_LENGTH} characters`);
    }
    if (replacement.length > exports.MAX_REPLACEMENT_LENGTH) {
        throw new Error(`@replace requires a "replacement" less than ${exports.MAX_REPLACEMENT_LENGTH} characters`);
    }
    pattern = pattern.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    return value.replace(new RegExp(pattern, flags), replacement);
}
registerDirective('@replace', (opts, payload) => {
    if (!(0, real_type_of_1.isObject)(opts)) {
        throw new Error('@replace requires an object with a "pattern" key');
    }
    if (!opts.pattern) {
        throw new Error('@replace requires a "pattern" key');
    }
    if (opts.replacement == null) {
        opts.replacement = '';
    }
    if (opts.pattern2 && opts.replacement2 == null) {
        opts.replacement2 = '';
    }
    if (opts.ignorecase == null) {
        opts.ignorecase = false;
    }
    if (opts.global == null) {
        opts.global = true;
    }
    const pattern = opts.pattern;
    const replacement = opts.replacement;
    const ignorecase = opts.ignorecase;
    const isGlobal = opts.global;
    if (opts.value) {
        let value = resolve(opts.value, payload);
        let new_value = '';
        if (typeof value === 'boolean' || typeof value === 'number') {
            value = String(value);
        }
        if (typeof value === 'string' &&
            typeof pattern === 'string' &&
            typeof replacement === 'string' &&
            typeof ignorecase === 'boolean' &&
            typeof isGlobal === 'boolean') {
            let flags = '';
            if (isGlobal) {
                flags += 'g';
            }
            if (ignorecase) {
                flags += 'i';
            }
            new_value = performReplace(value, pattern, replacement, flags);
            if (opts.pattern2 && typeof opts.pattern2 === 'string' && typeof opts.replacement2 === 'string') {
                new_value = performReplace(new_value, opts.pattern2, opts.replacement2, flags);
            }
        }
        return new_value;
    }
});
registerDirective('@arrayPath', (data, payload) => {
    if (!Array.isArray(data)) {
        throw new Error(`@arrayPath expected array, got ${(0, real_type_of_1.realTypeOf)(data)}`);
    }
    const [path, itemShape] = data;
    const root = typeof path === 'string' ? (0, get_1.get)(payload, path.replace('$.', '')) : resolve(path, payload);
    if (['object', 'array'].includes((0, real_type_of_1.realTypeOf)(root)) &&
        (0, real_type_of_1.realTypeOf)(itemShape) === 'object' &&
        Object.keys(itemShape).length > 0) {
        return (0, arrify_1.arrify)(root).map((item) => resolve(itemShape, item));
    }
    return root;
});
registerStringDirective('@path', (path, payload) => {
    return (0, get_1.get)(payload, path.replace('$.', ''));
});
registerStringDirective('@template', (template, payload) => {
    return (0, placeholders_1.render)(template, payload);
});
registerDirective('@literal', (value, payload) => {
    return resolve(value, payload);
});
registerDirective('@flatten', (opts, payload) => {
    if (!(0, real_type_of_1.isObject)(opts)) {
        throw new Error('@flatten requires an object with a "separator" key');
    }
    if (!opts.separator) {
        throw new Error('@flatten requires a "separator" key');
    }
    const separator = resolve(opts.separator, payload);
    if (typeof separator !== 'string') {
        throw new Error('@flatten requires a string separator');
    }
    const value = resolve(opts.value, payload);
    return (0, flatten_1.flattenObject)(value, '', separator, Boolean(opts.omitArrays));
});
registerDirective('@json', (opts, payload) => {
    if (!(0, real_type_of_1.isObject)(opts)) {
        throw new Error('@json requires an object with a "value" key');
    }
    if (!opts.mode) {
        throw new Error('@json requires a "mode" key');
    }
    if (!opts.value) {
        throw new Error('@json requires a "value" key');
    }
    const value = resolve(opts.value, payload);
    if (opts.mode === 'encode') {
        return JSON.stringify(value);
    }
    else if (opts.mode === 'decode') {
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            }
            catch (e) {
                return value;
            }
        }
        return value;
    }
});
registerDirective('@merge', (opts, payload) => {
    if (!(0, real_type_of_1.isObject)(opts)) {
        throw new Error('@merge requires an object with an "objects" key and a "direction" key');
    }
    if (!opts.direction) {
        throw new Error('@merge requires a "direction" key');
    }
    const direction = resolve(opts.direction, payload);
    if (!opts.objects) {
        throw new Error('@merge requires a "objects" key');
    }
    if (!Array.isArray(opts.objects))
        throw new Error(`@merge: expected opts.array, got ${typeof opts.objects}`);
    const objects = opts.objects.map((v) => resolve(v, payload));
    if (direction === 'left') {
        objects.reverse();
    }
    return Object.assign({}, ...objects);
});
registerDirective('@transform', (opts, payload) => {
    if (!(0, real_type_of_1.isObject)(opts)) {
        throw new Error('@transform requires an object with an "apply" key and a "mapping" key');
    }
    if (!opts.mapping) {
        throw new Error('@transform requires a "mapping" key');
    }
    if (!opts.apply) {
        throw new Error('@transform requires a "apply" key');
    }
    if (!(0, real_type_of_1.isObject)(opts.apply)) {
        throw new Error('@transform "apply" key should be an object');
    }
    const newPayload = transform(opts.apply, payload);
    return resolve(opts.mapping, newPayload);
});
registerDirective('@excludeWhenNull', (value, payload) => {
    const resolved = resolve(value, payload);
    if (resolved === null) {
        return undefined;
    }
    return cleanNulls(resolved);
});
registerDirective('@liquid', (opts, payload, statsContext) => {
    return (0, liquid_directive_1.evaluateLiquid)(opts, payload, statsContext);
});
function cleanNulls(value) {
    if ((0, real_type_of_1.isObject)(value)) {
        const cleaned = Object.assign({}, value);
        for (const key of Object.keys(value)) {
            const val = value[key];
            if (val === null) {
                cleaned[key] = undefined;
            }
            else if ((0, real_type_of_1.isObject)(val)) {
                cleaned[key] = cleanNulls(val);
            }
        }
        return cleaned;
    }
    return value;
}
function getMappingToProcess(mapping) {
    let mappingToProcess = { ...mapping };
    if (Object.keys(mapping).includes(ROOT_MAPPING_FIELD_KEY)) {
        const customerMappings = {};
        for (const key in mapping) {
            if (key !== ROOT_MAPPING_FIELD_KEY) {
                customerMappings[key] = mapping[key];
            }
        }
        mappingToProcess = mapping[ROOT_MAPPING_FIELD_KEY];
        if (Object.keys(mappingToProcess).length > 1) {
            throw new Error('The root mapping must only have a single directive object');
        }
        const rootDirective = mappingToProcess[Object.keys(mappingToProcess)[0]];
        if (!rootDirective || typeof rootDirective !== 'object') {
            throw new Error('The root directive must be an object');
        }
        rootDirective.mapping = customerMappings;
    }
    return mappingToProcess;
}
function resolve(mapping, payload, statsContext) {
    if (!(0, real_type_of_1.isObject)(mapping) && !(0, real_type_of_1.isArray)(mapping)) {
        return mapping;
    }
    if ((0, is_directive_1.isDirective)(mapping)) {
        if ((0, value_keys_1.isLiquidDirective)(mapping)) {
            return runDirective(mapping, payload, statsContext);
        }
        return runDirective(mapping, payload);
    }
    if (Array.isArray(mapping)) {
        return mapping.map((value) => resolve(value, payload, statsContext));
    }
    const resolved = {};
    for (const key of Object.keys(mapping)) {
        let originalTags = [];
        const statsTagsExist = statsContext?.tags !== undefined;
        if (statsTagsExist) {
            originalTags = statsContext.tags;
            statsContext.tags = [...statsContext.tags, `fieldKey:${key}`];
        }
        resolved[key] = resolve(mapping[key], payload, statsContext);
        if (statsTagsExist) {
            statsContext.tags = originalTags;
        }
    }
    return resolved;
}
function transform(mapping, data = {}, statsContext) {
    const realType = (0, real_type_of_1.realTypeOf)(data);
    if (realType !== 'object') {
        throw new Error(`data must be an object, got ${realType}`);
    }
    const mappingToProcess = getMappingToProcess(mapping);
    (0, validate_1.default)(mappingToProcess);
    const resolved = resolve(mappingToProcess, data, statsContext);
    const cleaned = (0, remove_undefined_1.removeUndefined)(resolved);
    return cleaned;
}
function transformBatch(mapping, data = [], statsContext) {
    const realType = (0, real_type_of_1.realTypeOf)(data);
    if (!(0, real_type_of_1.isArray)(data)) {
        throw new Error(`data must be an array, got ${realType}`);
    }
    const mappingToProcess = getMappingToProcess(mapping);
    (0, validate_1.default)(mappingToProcess);
    const resolved = data.map((d) => resolve(mappingToProcess, d, statsContext));
    return (0, remove_undefined_1.removeUndefined)(resolved);
}
//# sourceMappingURL=index.js.map